import type { Context, InputValue } from '../../contract/index.js';

/**
 * The custom element a `script` entry mounts in the slot. The vendor's script
 * defines it. tenant-config and the Admin hold their own copy of these rules
 * and messages, which must stay equal.
 */
export interface ScriptElement {
  readonly tag: string;
  /** Literal values, or values with `{name}` placeholders for declared inputs. */
  readonly attributes?: Readonly<Record<string, string>>;
}

export const ELEMENT_TAG_PATTERN = /^[a-z][a-z0-9._]*-[a-z0-9._-]*$/;

/** Hyphenated names SVG and MathML already use, which `customElements.define` refuses. */
export const RESERVED_ELEMENT_TAGS: readonly string[] = [
  'annotation-xml',
  'color-profile',
  'font-face',
  'font-face-src',
  'font-face-uri',
  'font-face-format',
  'font-face-name',
  'missing-glyph',
];

export const ELEMENT_ATTRIBUTE_NAME_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;

/** Attributes that run code, restyle, re-type or address the element. Event handlers (`on*`) are refused as well. */
export const DENIED_ELEMENT_ATTRIBUTES: readonly string[] = [
  'style',
  'is',
  'id',
  'class',
  'slot',
  'src',
  'href',
  'srcdoc',
];

export const ELEMENT_MAX_ATTRIBUTES = 16;
export const ELEMENT_ATTRIBUTE_VALUE_MAX_LENGTH = 500;

const PLACEHOLDER = /\{([A-Za-z][A-Za-z0-9_]{0,63})\}/g;
const ELEMENT_KEYS: readonly string[] = ['tag', 'attributes'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function attributeError(
  name: string,
  value: unknown,
  inputNames: readonly string[],
): string | null {
  if (!ELEMENT_ATTRIBUTE_NAME_PATTERN.test(name)) {
    return 'element attribute names must be lowercase letters, digits and -, start with a letter, and be at most 64 characters';
  }
  if (name.startsWith('on') || DENIED_ELEMENT_ATTRIBUTES.includes(name)) {
    return `element attribute ${name} is not allowed`;
  }
  if (typeof value !== 'string') {
    return 'element attributes must be an object of string values';
  }
  if (value.length > ELEMENT_ATTRIBUTE_VALUE_MAX_LENGTH) {
    return `element attribute values must be at most ${ELEMENT_ATTRIBUTE_VALUE_MAX_LENGTH} characters`;
  }
  let undeclared: string | undefined;
  const rest = value.replace(PLACEHOLDER, (_match, input: string) => {
    if (undeclared === undefined && !inputNames.includes(input)) {
      undeclared = input;
    }
    return '';
  });
  if (rest.includes('{') || rest.includes('}')) {
    return 'element attribute placeholders must be {inputName}';
  }
  if (undeclared !== undefined) {
    return `element attribute placeholder {${undeclared}} is not a declared input`;
  }
  return null;
}

/** Why an entry's `element` is refused, or null when it is valid. */
export function elementError(
  element: unknown,
  inputNames: readonly string[],
): string | null {
  if (
    !isRecord(element) ||
    Object.keys(element).some((key) => !ELEMENT_KEYS.includes(key))
  ) {
    return 'element must be an object with a tag and optional attributes';
  }
  const { tag, attributes } = element;
  if (typeof tag !== 'string' || !ELEMENT_TAG_PATTERN.test(tag)) {
    return 'element tag must be a custom element name: lowercase, starting with a letter, with at least one -';
  }
  if (RESERVED_ELEMENT_TAGS.includes(tag)) {
    return `element tag ${tag} is a reserved name`;
  }
  if (attributes === undefined) {
    return null;
  }
  if (!isRecord(attributes)) {
    return 'element attributes must be an object of string values';
  }
  const entries = Object.entries(attributes);
  if (entries.length > ELEMENT_MAX_ATTRIBUTES) {
    return `element must have at most ${ELEMENT_MAX_ATTRIBUTES} attributes`;
  }
  for (const [name, value] of entries) {
    const found = attributeError(name, value, inputNames);
    if (found) {
      return found;
    }
  }
  return null;
}

function text(value: InputValue): string {
  return Array.isArray(value) ? value.join(',') : String(value);
}

/**
 * Each declared attribute with its placeholders filled, or `undefined` when a
 * placeholder's input has no value, so the attribute is left out.
 */
export function elementAttributes(
  element: ScriptElement,
  inputs: Context['inputs'],
): Array<readonly [name: string, value: string | undefined]> {
  return Object.entries(element.attributes ?? {}).map(([name, template]) => {
    let missing = false;
    const value = template.replace(PLACEHOLDER, (_match, input: string) => {
      const filled = Object.prototype.hasOwnProperty.call(inputs, input)
        ? inputs[input]
        : undefined;
      if (filled === undefined) {
        missing = true;
        return '';
      }
      return text(filled);
    });
    return [name, missing ? undefined : value] as const;
  });
}
